import type { DeedView } from '@wyn/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from '@tanstack/react-router';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { PageTitle } from '../components/ui.tsx';
import { UI } from '../icons/ui-icons.ts';
import { deedsQuery } from '../lib/queries.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const when = (at: number) => new Date(at).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });

/** Deeds done (with their dates), the visible ones still to do (with progress), and "???" for each hidden one not yet found. */
export function Deeds() {
  const me = useMe();
  const deeds = useQuery(deedsQuery());
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  const d = deeds.data;
  const done = d?.deeds.filter((x) => x.doneAt !== null) ?? [];
  const open = d?.deeds.filter((x) => x.doneAt === null) ?? [];

  return (
    <GameShell me={me.data} skew={me.skew}>
      <div className="space-y-6">
        <section className="panel space-y-3">
          <PageTitle icon={UI.deeds}>Deeds</PageTitle>
          <ErrorNotice error={deeds.error} />
          {!d ? (
            <p className="text-ink-faint">{deeds.isPending ? 'Reading the roll of deeds…' : ''}</p>
          ) : (
            <>
              <p className="text-ink-dim" data-testid="deeds-count">
                {d.done} of {d.total} done.
                {d.hiddenLeft > 0 && <span className="text-ink-faint"> {d.hiddenLeft} hidden deeds still to find.</span>}
              </p>
              <p className="text-sm text-ink-faint">
                Deeds that bring a title let you go by it: choose one on your <Link to="/character" className="text-gilt">character sheet</Link>.
              </p>
            </>
          )}
        </section>

        {done.length > 0 && (
          <section className="panel" aria-labelledby="deeds-done">
            <h2 id="deeds-done" className="heading mb-2">Done</h2>
            <ol className="space-y-2">
              {done.map((x) => <Deed key={x.key} deed={x} />)}
            </ol>
          </section>
        )}

        {open.length > 0 && (
          <section className="panel" aria-labelledby="deeds-open">
            <h2 id="deeds-open" className="heading mb-2">Still to do</h2>
            <ol className="space-y-2">
              {open.map((x) => <Deed key={x.key} deed={x} />)}
            </ol>
          </section>
        )}

        {d && d.hiddenLeft > 0 && (
          <section className="panel space-y-2" aria-labelledby="deeds-hidden">
            <h2 id="deeds-hidden" className="heading">Hidden</h2>
            <p className="text-sm text-ink-faint">Nobody will tell you what these are. You will know when you have done one.</p>
            <ul className="flex flex-wrap gap-2" aria-label={`${d.hiddenLeft} hidden deeds`}>
              {Array.from({ length: d.hiddenLeft }, (_, i) => (
                <li key={i} className="border border-rule px-2 py-0.5 font-mono text-sm text-ink-faint">???</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </GameShell>
  );
}

function Deed({ deed: x }: { deed: DeedView }) {
  const done = x.doneAt !== null;
  return (
    <li data-testid="deed" className="space-y-1 border-b border-rule/60 pb-2 last:border-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className={done ? 'text-gilt' : ''}>
          {x.name}
          {x.hidden && <span className="ml-2 text-xs text-ink-faint">hidden</span>}
        </span>
        {done ? (
          <time className="font-mono text-xs text-ink-faint" dateTime={new Date(x.doneAt!).toISOString()}>{when(x.doneAt!)}</time>
        ) : (
          <span className="font-mono text-xs tabular-nums text-ink-faint">
            {x.progress.have.toLocaleString('en-GB')}/{x.progress.need.toLocaleString('en-GB')}
          </span>
        )}
      </div>
      <p className="text-sm text-ink-dim">{done ? x.flavour : x.hint}</p>
      {!done && (
        <div className="h-1 bg-rule" role="progressbar" aria-label={x.name} aria-valuenow={x.progress.have} aria-valuemax={x.progress.need}>
          <div className="h-full bg-gilt" style={{ width: `${Math.min(100, (x.progress.have / x.progress.need) * 100)}%` }} />
        </div>
      )}
      {x.reward && <p className="text-xs text-ink-faint">{x.reward}</p>}
    </li>
  );
}
